import { Box, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Tooltip,
  XAxis,
  YAxis,
  Area,
  ReferenceLine,
} from "recharts";
import { useContextDashboardBrokerContext } from "src/Contexts/Dashboard/ContextDashboardBroker";
import { CardCore } from "src/Pages/components/CardCore/CardCore";
import { FormatMoney } from "src/shared/Utils/FormatMoney";

import { FormMonthByMonthProfitChart } from "./Form";
import { CustomTooltipProfit } from "./CustomTooltip";

const COLORS = {
  positive: "#4361EE",
  negative: "#EF4444",
  grid: "#F1F1F4",
  axis: "#9CA3AF",
};

export const MonthByMonthProfitChart = () => {
  const { dataProfitableValues } = useContextDashboardBrokerContext();

  const gradientOffset = () => {
    const dataMax = Math.max(...dataProfitableValues.data.map((i) => i.value));
    const dataMin = Math.min(...dataProfitableValues.data.map((i) => i.value));

    if (dataMax <= 0) return 0;
    if (dataMin >= 0) return 1;

    return dataMax / (dataMax - dataMin);
  };

  const off = gradientOffset();

  return (
    <CardCore sxStyle={{ margin: "1rem 0", marginBottom: "24px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Lucro de parcelas
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#9CA3AF",
              fontWeight: 500,
              backgroundColor: "#F3F4F6",
              px: 1.2,
              py: 0.3,
              borderRadius: "6px",
            }}
          >
            comparação
          </Typography>
        </Box>

        <FormMonthByMonthProfitChart />
      </Box>

      <ResponsiveContainer height={320} width="100%">
        <AreaChart
          data={dataProfitableValues.data}
          margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
        >
          <defs>
            <linearGradient id="profitGradient" x1="0" x2="0" y1="0" y2="1">
              <stop
                offset={off}
                stopColor={COLORS.positive}
                stopOpacity={0.1}
              />
              <stop
                offset={off}
                stopColor={COLORS.negative}
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient id="profitStroke" x1="0" x2="0" y1="0" y2="1">
              <stop offset={off} stopColor={COLORS.positive} stopOpacity={1} />
              <stop offset={off} stopColor={COLORS.negative} stopOpacity={1} />
            </linearGradient>
          </defs>

          <CartesianGrid
            horizontal
            stroke={COLORS.grid}
            strokeDasharray="4 4"
            vertical={false}
          />

          <XAxis
            axisLine={false}
            dataKey="label"
            dy={8}
            fontSize={12}
            stroke={COLORS.axis}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            fontSize={11}
            stroke={COLORS.axis}
            tickFormatter={(value) => FormatMoney(String(value.toFixed(2)))}
            tickLine={false}
          />

          <ReferenceLine
            stroke="#D1D5DB"
            strokeDasharray="3 3"
            y={0}
          />

          <Tooltip
            content={<CustomTooltipProfit />}
            cursor={{ stroke: COLORS.axis, strokeDasharray: "4 4" }}
          />

          <Area
            activeDot={{ r: 5, strokeWidth: 2, fill: "#fff" }}
            baseValue={0}
            dataKey="value"
            dot={false}
            fill="url(#profitGradient)"
            fillOpacity={1}
            name="Lucro"
            stroke="url(#profitStroke)"
            strokeWidth={2.5}
            type="monotone"
          />
        </AreaChart>
      </ResponsiveContainer>
    </CardCore>
  );
};
